import { Grid, TextField, styled, IconButton, Box } from '@mui/material';
import {
  ColumnDef,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  RowSelectionState,
} from '@tanstack/table-core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import TableComponent from 'src/components/collapsible-table/table';
import { useReactTable } from '@tanstack/react-table';
import {
  Header,
  InputType,
  TableComponent as TableComp,
} from '../../types/generic-table.model';
import {
  handleTopLineInputValue,
  handleTopLineRowInputValue,
  updateApplyValueForTopLine,
  updateResetValueForTopLine,
} from '../../store/pricing-simulator-slice';
import { getFormattedValueForInput, isValueEqual } from './topline-input.util';
import ToplineSelectInputBox from './topline-select-input-box';
import { IMPLEMENTATION_PERIOD_1_KEY, IMPLEMENTATION_PERIOD_2_KEY } from 'src/constants';

interface ExtendedHeader extends Header {
  groupAlias?: string;
}

interface ITopLineInputTableProps {
  currActiveFieldVal: {
    field1: [{ label: string; value: string }] | any;
    field2: [{ label: string; value: string }] | any;
  };
  onPeriodChange?: (col: string, val: string | number) => void;
}

const StyledTextField = styled(TextField)`
  input {
    font-size: 10px;
    background-color: #fcfcfc;
  }
`;

export default function TopLineInputsTable({
  currActiveFieldVal,
  onPeriodChange
}: ITopLineInputTableProps) {
  const appDispatch = useAppDispatch();
  const { topLineInputTableData, kpi2ColumnMapping } = useAppSelector(
    state => state.pricingSimulator
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // "allColumns" are the groups (with empty parent_field) that contain children.
  const allColumns = useMemo(() => {
    return topLineInputTableData.header.filter(
      item => item.parent.parent_field === '' && item.children.length > 0
    );
  }, [topLineInputTableData.header]);

  /**
   * getVisibleColumns:
   *   - For each group, by default only show the child whose parent's parent_field is either
   *     "pricing_implement_after_cal_1" or "pricing_implement_after_cal_2".
   *   - If the group is expanded then show the remaining children as well.
   *
   * Note: We attach the parent group's alias via groupAlias for later reference.
   */
  const getVisibleColumns = (): ExtendedHeader[] => {
    let visibleHeaders: ExtendedHeader[] = [];
    allColumns.forEach(group => {
      const childrenWithGroup: ExtendedHeader[] = group.children.map(
        (child: Header) => ({
          ...child,
          groupAlias: group.parent.alias,
        })
      );
      // Only show defaults based on the constant parent_field values.
      const defaultChildren = childrenWithGroup.filter(
        child =>
          child.parent.parent_field === IMPLEMENTATION_PERIOD_1_KEY ||
          child.parent.parent_field === IMPLEMENTATION_PERIOD_2_KEY
      );
      visibleHeaders.push(...defaultChildren);
      // When expanded, add the remaining children.
      if (expanded[group.parent.alias]) {
        const additionalChildren = childrenWithGroup.filter(
          child =>
            child.parent.parent_field !== IMPLEMENTATION_PERIOD_1_KEY &&
            child.parent.parent_field !== IMPLEMENTATION_PERIOD_2_KEY
        );
        visibleHeaders.push(...additionalChildren);
      }
    });
    return visibleHeaders;
  };

  const columnsToTable = useMemo(
    () => getVisibleColumns(),
    [expanded, allColumns]
  );

  /**
   * Toggling an expand icon will collapse all groups first, then expand the selected group.
   */
  const handleExpand = (groupAlias: string) => {
    setExpanded(prev => {
      const isCurrentlyExpanded = prev[groupAlias];
      const newState: Record<string, boolean> = {};
      allColumns.forEach(col => {
        newState[col.parent.alias] = false;
      });
      if (!isCurrentlyExpanded) {
        newState[groupAlias] = true;
      }
      return newState;
    });
  };

  const getFieldColumns = () => {
    let fieldColArray = [];
    Object.keys(currActiveFieldVal).forEach(key => {
      if (currActiveFieldVal[key]?.length) {
        fieldColArray.push({
          header: ({ column }) => (
            <Grid container justifyContent='center'>
              <Grid item textAlign='center' xs={12}>
                {currActiveFieldVal[key][0]?.label}
              </Grid>
              <Grid item xs={12} px={1}>
                <StyledTextField
                  fullWidth
                  size='small'
                  variant='outlined'
                  placeholder={`Search ${currActiveFieldVal[key][0]?.label}`}
                  onChange={e => {
                    column.setFilterValue(e.target.value);
                  }}
                />
              </Grid>
            </Grid>
          ),
          meta: { className: 'text-column table-col' },
          accessorKey: `parent.data.${currActiveFieldVal[key][0]?.value}`,
          cell: (info: any) => info.getValue(),
        });
      }
    });
    return fieldColArray;
  };

  const handleRowInputChange = (
    newValue: number,
    columnName: string,
    rowData: any
  ) => {
    if (newValue !== undefined) {
      appDispatch(
        handleTopLineRowInputValue({
          newValue,
          columnName,
          rowData,
          field1: currActiveFieldVal.field1[0].value,
          field2: currActiveFieldVal.field2.length
            ? currActiveFieldVal.field2[0].value
            : 0,
        })
      );
    }
  };


  const CellComponent = ({ row, colData }) => {
    const parentField = colData.parent.parent_field;
    const numberFormat = colData.parent.number_format;
    const rawValue = row.original.parent.data[parentField];
    // const topLineInputTableGlobalValue = useAppSelector(
    //   state => state.pricingSimulator.topLineInputTableGlobalValue
    // );
    let dropdownData = [];

    const formattedValue = getFormattedValueForInput(
      typeof rawValue === 'number' && !isNaN(rawValue) ? rawValue : 0,
      numberFormat
    );
    if (parentField === IMPLEMENTATION_PERIOD_1_KEY) {
      dropdownData = [...colData.parent.static_values];
    } else if (parentField === IMPLEMENTATION_PERIOD_2_KEY) {
      const implementPeriod1 =
        row?.original?.parent?.data?.[IMPLEMENTATION_PERIOD_1_KEY];
      dropdownData = colData.parent.static_values
        .filter(option => option.value > implementPeriod1)
        .sort((a, b) => (a.value > b.value ? 1 : -1));
    }

    const [value, setValue] = useState(formattedValue);

    const globalResetFlag = useAppSelector(
      state => state.pricingSimulator.isResettingTopLineInput
    );

    const { kpi2ColumnMapping } = useAppSelector((state) => state.pricingSimulator);
    let isDisabled = false;
    if (!row?.original?.parent?.data?.[IMPLEMENTATION_PERIOD_2_KEY]
      || row?.original?.parent?.data?.[IMPLEMENTATION_PERIOD_2_KEY] === 0
      || row?.original?.parent?.data?.[IMPLEMENTATION_PERIOD_2_KEY] === '-') {
      const columnId = parentField || '';
      if (kpi2ColumnMapping.includes(columnId)) isDisabled = true;
    }

    useEffect(() => {
      setValue(formattedValue);
    }, [formattedValue, globalResetFlag]);

    const handleInputChange = (input: string) => {
      setValue(input === '' ? '-' : input);
      appDispatch(updateApplyValueForTopLine());
    };
    const handleSelectChange = val => {
      appDispatch(updateApplyValueForTopLine());
      handleRowInputChange(
        Number(val),
        parentField,
        row.original.parent.data
      );
    };

    const handleBlur = () => {
      const commitValue =
        value === '-' || value === '' || isNaN(Number(value))
          ? 0
          : Number(value);
      handleRowInputChange(
        commitValue,
        parentField,
        row.original.parent.data
      );
    };
    switch (colData.parent.field_format) {
      case InputType.SINGLESELECT_DROPDOWN:
        return (
          <ToplineSelectInputBox
            handleSelectChange={handleSelectChange}
            formattedValue={formattedValue}
            globalResetFlag={globalResetFlag}
            selectValues={dropdownData}
            inputsDisabled={dropdownData.length === 0 ? true : false}
          />
        );
      default:
        return (
          <div className='input-box-container'>
            <input
              value={value === '' || value === '-' ? '-' : value}
              className='table-row-input-box'
              onChange={e => handleInputChange(e.target.value)}
              onBlur={handleBlur}
              type='number'
              placeholder='-'
              disabled={isDisabled}
            />
          </div>
        );
    }
  };

  const columns: ColumnDef<any>[] = useMemo(() => {
    return [
      ...getFieldColumns(),
      ...columnsToTable
        .filter(col => col.parent.is_visible && col.parent.order > 3)
        .map((colData: ExtendedHeader) => ({
          id: `col_${colData.parent.alias}_${colData.groupAlias || ''}`,
          header: ({ table }) => {
            const [value, setValue] = useState<number | string>();
            const { isResettingTopLineInput, topLineInputTableGlobalValue } =
              useAppSelector(state => state.pricingSimulator);
            useEffect(() => {
              if (isResettingTopLineInput) {
                setValue('-');
              }
            }, [isResettingTopLineInput]);

            let dropdownData = [];
            let formattedValue = null;
            if (
              colData.parent.parent_field === IMPLEMENTATION_PERIOD_1_KEY
            ) {
              dropdownData = [...colData.parent.static_values];
              formattedValue =
                topLineInputTableGlobalValue[IMPLEMENTATION_PERIOD_1_KEY] ??
                null;
            } else if (
              colData.parent.parent_field === IMPLEMENTATION_PERIOD_2_KEY
            ) {
              formattedValue =
                topLineInputTableGlobalValue[IMPLEMENTATION_PERIOD_2_KEY] ??
                null;
              dropdownData = colData.parent.static_values
                .filter(
                  option =>
                    option.value >
                    topLineInputTableGlobalValue[IMPLEMENTATION_PERIOD_1_KEY]
                )
                .sort((a, b) => (a.value > b.value ? 1 : -1));
            }
            const handleHeaderInputChange = (
              newValue: number | string,
              columnName: string
            ) => {
              const filteredRows = table.getFilteredRowModel()?.rows?.length
                ? table
                  .getFilteredRowModel()
                  .rows.map(row => row.original.parent.data.index)
                : [];
              if (newValue !== undefined) {
                appDispatch(
                  handleTopLineInputValue({
                    newValue,
                    columnName,
                    filteredRows,
                  })
                );
              }
            };
            const handleSelectHeaderInputChange = (
              newValue: number | string,
              columnName: string
            ) => {
              appDispatch(updateApplyValueForTopLine());
              if (
                columnName === IMPLEMENTATION_PERIOD_1_KEY ||
                columnName === IMPLEMENTATION_PERIOD_2_KEY
              ) {
                //store the new value on change
                onPeriodChange(columnName, newValue);
              }
              const filteredRows = table.getFilteredRowModel()?.rows?.length
                ? table
                  .getFilteredRowModel()
                  .rows.map(row => row.original.parent.data.index)
                : [];
              if (newValue !== undefined) {
                appDispatch(
                  handleTopLineInputValue({
                    newValue,
                    columnName,
                    filteredRows,
                  })
                );
              }
            };


            let isDisabled = false;
            if (!topLineInputTableGlobalValue[IMPLEMENTATION_PERIOD_2_KEY]
              || topLineInputTableGlobalValue[IMPLEMENTATION_PERIOD_2_KEY] === 0) {
              const columnId = colData.parent.parent_field || '';
              if (kpi2ColumnMapping.includes(columnId)) isDisabled = true;
            }
            // Determine if this is a default column for expansion based on parent_field.
            const isDefaultExpandColumn =
              colData.parent.parent_field === IMPLEMENTATION_PERIOD_1_KEY ||
              colData.parent.parent_field === IMPLEMENTATION_PERIOD_2_KEY;

            // Find the group info in order to check for extra children.
            const groupInfo = allColumns.find(
              group => group.parent.alias === colData.groupAlias
            );
            const hasAdditionalChildren =
              groupInfo &&
              groupInfo.children.some(
                (child: Header) =>
                  child.parent.parent_field !==
                  IMPLEMENTATION_PERIOD_1_KEY &&
                  child.parent.parent_field !== IMPLEMENTATION_PERIOD_2_KEY
              );

            return (
              <Grid
                container
                spacing={1}
                justifyContent='center'
                alignItems='center'
                direction='column'
              >
                <Grid item xs={12}>
                  <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                    onClick={() =>
                      isDefaultExpandColumn &&
                      hasAdditionalChildren &&
                      handleExpand(colData.groupAlias || '')
                    }
                    sx={{
                      cursor:
                        isDefaultExpandColumn && hasAdditionalChildren
                          ? 'pointer'
                          : 'default',
                    }}
                  >
                    <span>{colData.parent.alias}</span>
                    {isDefaultExpandColumn && hasAdditionalChildren && (
                      <IconButton size='small' sx={{ ml: 1 }}>
                        <img
                          src='/static/images/multi-price-expand-icon.svg'
                          className='expand-icon'
                          alt='expand_icon'
                          style={{
                            transform: expanded[colData.groupAlias || '']
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)',
                          }}
                        />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  {colData.parent.field_format ===
                    InputType.SINGLESELECT_DROPDOWN ? (
                    <ToplineSelectInputBox
                      formattedValue={formattedValue}
                      globalResetFlag={isResettingTopLineInput}
                      selectValues={dropdownData}
                      inputsDisabled={dropdownData.length === 0 ? true : false}
                      handleSelectChange={(val: number) => {
                        handleSelectHeaderInputChange(
                          val,
                          colData.parent.parent_field
                        );
                      }}
                    />
                  ) : (
                    <div className='input-box-container'>
                      <input
                        value={Number.isNaN(value) ? '-' : value}
                        onChange={e => {
                          setValue(Number(e.target.value));
                          appDispatch(updateApplyValueForTopLine());
                        }}
                        onBlur={() =>
                          handleHeaderInputChange(
                            value,
                            colData.parent.parent_field
                          )
                        }
                        className='table-row-input-box'
                        type='number'
                        placeholder='-'
                        disabled={isDisabled}
                      />
                    </div>
                  )}
                </Grid>
              </Grid>
            );
          },
          meta: {
            className: 'text-center',
            headerStyle: {
              backgroundColor: expanded[colData.groupAlias || '']
                ? '#C7C5FF'
                : '#f2f2f2',
            },
          },
          accessorKey: `parent.data.${colData.parent.parent_field}`,

          cell: ({ row }) => (
            <CellComponent row={row} colData={colData} />
          ),
        })),
    ];
  }, [columnsToTable, expanded, allColumns]);

  const tableRef = useRef<HTMLTableElement>();
  const table = useReactTable({
    data: topLineInputTableData.row,
    columns,
    state: { expanded, rowSelection },
    onExpandedChange: updater => {
      setExpanded(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (typeof next === 'object' && next !== null && !Array.isArray(next)) {
          return next as Record<string, boolean>;
        }
        return prev;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <TableComponent
      setHeightExplicitly={false}
      table={table}
      extClassName='top-line-table'
      tableRef={tableRef}
    />
  );
}
